import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Breadcrumb from '../components/Breadcrumb';
import ProjectCharts from '../components/charts/ProjectCharts';
import { 
  ArrowLeft, 
  ExternalLink, 
  Github, 
  Calendar, 
  Clock,
  Target,
  Lightbulb,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ImageIcon
} from 'lucide-react';
import { portfolioData } from '../data/mockdata';
import { getProjectBySlug, getNextProject, getPreviousProject, getStatusColor, getCategoryColor } from '../utils/projectUtils';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [selectedTab, setSelectedTab] = useState("overview");
  const [selectedImage, setSelectedImage] = useState(0);

  const project = getProjectBySlug(portfolioData.projects, slug);
  
  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const nextProject = getNextProject(portfolioData.projects, project.id);
  const previousProject = getPreviousProject(portfolioData.projects, project.id);

  const breadcrumbItems = [
    { label: 'Projects', href: '/projects' },
    { label: project.title }
  ];

  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} />

        {/* Hero Section */}
        <div className="mb-12">
          <Card className="glassmorphism overflow-hidden">
            <div className="relative h-64 md:h-96 bg-gradient-to-r from-blue-500 to-purple-600">
              <img 
                src={project.heroImage} 
                alt={project.title}
                className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="p-6 md:p-8 text-white">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge className={getStatusColor(project.status)}>
                      {project.status}
                    </Badge>
                    <Badge className={getCategoryColor(project.category)}>
                      {project.type}
                    </Badge>
                  </div>
                  <h1 className="text-3xl md:text-5xl font-bold mb-4">
                    {project.title}
                  </h1>
                  <p className="text-lg md:text-xl text-gray-200 max-w-3xl">
                    {project.description}
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {project.duration}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {project.status}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Link to="/projects">
            <Button variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Projects
            </Button>
          </Link>
          {project.githubUrl && (
            <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="flex items-center gap-2">
                <Github className="h-4 w-4" />
                View Code
              </Button>
            </a>
          )}
          {project.liveUrl && (
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">
              <Button className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                Live Demo
              </Button>
            </a>
          )}
        </div>

        {/* Main Content Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="mb-12">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full max-w-4xl mx-auto mb-8">  {/*Also Replace grid-cols 4 to 5 */}   
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="methodology">Methodology</TabsTrigger>
            <TabsTrigger value="findings">Key Findings</TabsTrigger>
 {/*---------<TabsTrigger value="visualizations">Charts</TabsTrigger>------*/}
            <TabsTrigger value="gallery">Gallery</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Problem Statement */}
              <Card className="glassmorphism lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    Problem Statement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {project.problemStatement}
                  </p>
                </CardContent>
              </Card>

              {/* Project Stats */}
              <Card className="glassmorphism">
                <CardHeader>
                  <CardTitle>Project Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Duration</div>
                    <div className="font-semibold">{project.duration}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Status</div>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Impact</div>
                    <div className="font-semibold text-green-600 dark:text-green-400">
                      {project.impact}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Tools Used</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {project.tools.map((tool) => (
                        <Badge key={tool} variant="secondary" className="text-xs">
                          {tool}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Objectives */}
            <Card className="glassmorphism">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" />
                  Project Objectives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.objectives?.map((objective, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">{objective}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Key Highlights */}
            <Card className="glassmorphism">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                  Key Highlights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-purple-600 rounded-full flex-shrink-0 mt-2"></div>
                      <span className="text-gray-700 dark:text-gray-300">{highlight}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Methodology Tab */}
          <TabsContent value="methodology" className="space-y-8">
            {project.methodology && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="glassmorphism">
                  <CardHeader>
                    <CardTitle>Data Collection</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 dark:text-gray-300">
                      {project.methodology.dataCollection}
                    </p>
                  </CardContent>
                </Card>

                <Card className="glassmorphism">
                  <CardHeader>
                    <CardTitle>Data Processing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 dark:text-gray-300">
                      {project.methodology.dataProcessing}
                    </p>
                  </CardContent>
                </Card>

                <Card className="glassmorphism">
                  <CardHeader>
                    <CardTitle>Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 dark:text-gray-300">
                      {project.methodology.analysis}
                    </p>
                  </CardContent>
                </Card>

                <Card className="glassmorphism">
                  <CardHeader>
                    <CardTitle>Visualization</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 dark:text-gray-300">
                      {project.methodology.visualization}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Technical Challenges */}
            {project.technicalChallenges && (
              <Card className="glassmorphism">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                    Technical Challenges & Solutions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {project.technicalChallenges.map((item, index) => (
                      <div key={index} className="border-l-4 border-orange-500 pl-4">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                          {item.challenge}
                        </h4>
                        <p className="text-gray-700 dark:text-gray-300">
                          {item.solution}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Key Findings Tab */}
          <TabsContent value="findings" className="space-y-8">
            {project.keyFindings && (
              <div className="space-y-6">
                {project.keyFindings.map((finding, index) => (
                  <Card key={index} className="glassmorphism">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-yellow-600" />
                        {finding.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 dark:text-gray-300 mb-3">
                        {finding.description}
                      </p>
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border-l-4 border-green-500">
                        <div className="text-sm font-medium text-green-800 dark:text-green-300">
                          Impact: {finding.impact}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Recommendations */}
            {project.recommendations && (
              <Card className="glassmorphism">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                    Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {project.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <ArrowRight className="h-4 w-4 text-blue-600 flex-shrink-0 mt-1" />
                        <span className="text-gray-700 dark:text-gray-300">{rec}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results */}
            {project.results && (
              <Card className="glassmorphism">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Results & Impact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {project.results.map((result, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-1" />
                        <span className="text-gray-700 dark:text-gray-300">{result}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Next Steps */}
            {project.nextSteps && (
              <Card className="glassmorphism">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowRight className="h-5 w-5 text-purple-600" />
                    Next Steps
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {project.nextSteps.map((step, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {index + 1}
                        </div>
                        <span className="text-gray-700 dark:text-gray-300">{step}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Visualizations Tab */}
  {/*-----<TabsContent value="visualizations">
            <Card className="glassmorphism">
              <CardHeader>
                <CardTitle>Data Visualizations</CardTitle>
                <p className="text-gray-600 dark:text-gray-400">
                  Interactive charts and graphs showcasing key insights from the analysis
                </p>
              </CardHeader>
              <CardContent>
                <ProjectCharts projectSlug={project.slug} />
              </CardContent>
            </Card>
          </TabsContent>---------*/}

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            {project.gallery && (
              <div className="space-y-6">
                <Card className="glassmorphism">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ImageIcon className="h-5 w-5" />
                      Project Gallery
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Featured Image */}
                    <div className="mb-6">
                      <img 
                        src={project.gallery[selectedImage]} 
                        alt={`${project.title} screenshot ${selectedImage + 1}`}
                        className="w-full h-66 md:h-98 object-cover rounded-lg"
                      />
                    </div>

                    {/* Thumbnail Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {project.gallery.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`relative overflow-hidden rounded-lg transition-all ${
                            selectedImage === index 
                              ? 'ring-2 ring-blue-500' 
                              : 'hover:opacity-80'
                          }`}
                        >
                          <img 
                            src={image} 
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-24 object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Navigation Between Projects */}
        <div className="flex justify-between items-center">
          <Link to={`/projects/${previousProject.slug}`}>
            <Button variant="outline" className="flex items-center gap-2">
              <ChevronLeft className="h-4 w-4" />
              Previous: {previousProject.title}
            </Button>
          </Link>
          
          <Link to={`/projects/${nextProject.slug}`}>
            <Button variant="outline" className="flex items-center gap-2">
              Next: {nextProject.title}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;